import { HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { match } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { withLoading } from 'utils';
import api from 'api';
import SupportPhone from 'components/common/SupportPhone';
import RegisterHeader from '../RegisterHeader';
import useEmailsLocales from '../use-emails-locales';
import useRegisterLocales from '../use-register-locales';
import pageClasses from '../Register.module.scss';
import classes from './FinishCustomProductRegistration.module.scss';

type Props = {
  history: any;
  match: match<{ pendingRegistration: string }>;
};

type Values = {
  email: string;
  messageLocale: string;
  agreeReceiveTextMessages: boolean;
  phone?: string;
  parentFirstName: string;
  parentLastName: string;
  parentUsername: string;
  childFirstName: string;
  childLastName: string;
  childUsername: string;
  instructionLanguage: string;
  secondInstructionLanguage: string;
  registerLocale?: string;
};

type LocaleOption = { label: string; value: string };

// Copy intentionally follows the approved desktop Custom Product Register concept.
// Dynamic values such as languages and support phone are resolved from existing configuration below.
const CUSTOM_REGISTRATION_COPY = {
  adultFirstName: 'Adult First Name: (At least 3 letters)',
  adultLastName: 'Adult Last Name: (At least 3 letters)',
  childFirstName: 'Child First Name: (At least 3 letters)',
  childLastName: 'Child Last Name: (At least 3 letters)',
  username: 'Username: (At least 3 letters)',
  instructionLanguage: 'Instruction language',
  secondInstructionLanguage: 'Second Instruction language',
  language: 'Language',
  secondLanguageDescription:
    'The fastest way for children to read and write in a second language is read and write as best they can in their own language.',
} as const;

const FinishCustomProductRegistration = ({ match, history }: Props) => {
  const dispatch = useDispatch();
  const rawTranslate = useSelector((state: any) => state.status.translate);
  const currentLanguage = useSelector((state: any) => state.status.lang);
  const commonLocales = useSelector((state: any) => state.common.locales || []);
  const commonProducts = useSelector((state: any) => state.common.products || []);
  const messageLocales = useEmailsLocales();
  const configuredInstructionLocales = useRegisterLocales();
  const pendingId = match.params.pendingRegistration;
  const [processing, setProcessing] = useState(true);
  const [pageError, setPageError] = useState('');
  const [pendingRegistration, setPendingRegistration] = useState<any>(null);
  const autoFinishRef = useRef(false);

  const t = useCallback(
    (id: number, fallback: string) => {
      const translated = typeof rawTranslate === 'function' ? rawTranslate(id, fallback) : fallback;
      return typeof translated === 'string' && translated.trim() ? translated : fallback;
    },
    [rawTranslate],
  );

  const instructionLocales: LocaleOption[] = useMemo(() => {
    const pendingProductId = String(
      pendingRegistration?.productId?._id || pendingRegistration?.productId || '',
    );
    const product = commonProducts.find((item: any) => String(item?._id || '') === pendingProductId);
    const productLocales = Object.entries(product?.defaultSolutionsForMembers || {})
      .filter(([, solutionId]) => !!solutionId)
      .map(([code]) => code);

    if (!productLocales.length) {
      return configuredInstructionLocales;
    }

    const options = productLocales
      .map((code) => {
        const locale = commonLocales.find((item: any) => item.code === code);
        return locale
          ? { value: locale.code, label: locale.displayName || locale.name || locale.code }
          : null;
      })
      .filter(Boolean) as LocaleOption[];

    return options.length ? options : configuredInstructionLocales;
  }, [commonLocales, commonProducts, configuredInstructionLocales, pendingRegistration]);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    defaultValues: {
      messageLocale: '',
      instructionLanguage: '',
      secondInstructionLanguage: '',
      agreeReceiveTextMessages: false,
    },
  });

  const acceptsTexts = watch('agreeReceiveTextMessages');
  const phone = watch('phone');
  const messageLocale = watch('messageLocale');
  const instructionLanguage = watch('instructionLanguage');
  const secondInstructionLanguage = watch('secondInstructionLanguage');

  useEffect(() => {
    if (!acceptsTexts) {
      if (phone) setValue('phone', '');
      return;
    }

    if (!phone || !phone.match(/^\+1/)) {
      setValue('phone', '+1');
    }
  }, [acceptsTexts, phone, setValue]);

  useEffect(() => {
    if (!messageLocales.length) return;
    const options = messageLocales.map((locale: LocaleOption) => locale.value);
    if (!options.includes(messageLocale)) {
      const nextLocale = options.includes(currentLanguage?.code) ? currentLanguage.code : options[0];
      setValue('messageLocale', nextLocale);
    }
  }, [currentLanguage?.code, messageLocale, messageLocales, setValue]);

  useEffect(() => {
    if (!instructionLocales.length) return;
    const options = instructionLocales.map((locale) => locale.value);
    const nextLocale = options.includes(currentLanguage?.code) ? currentLanguage.code : options[0];

    if (!options.includes(instructionLanguage)) {
      setValue('instructionLanguage', nextLocale);
    }
    if (!options.includes(secondInstructionLanguage)) {
      setValue('secondInstructionLanguage', nextLocale);
    }
  }, [currentLanguage?.code, instructionLanguage, instructionLocales, secondInstructionLanguage, setValue]);

  const finishWithoutForm = useCallback(async () => {
    if (autoFinishRef.current) return;
    autoFinishRef.current = true;
    const result = await withLoading(
      dispatch,
      async () => api.pendingRegistration.finishRegistration(pendingId, {}),
    );
    if (result?.success && result?.redirectTo) {
      window.location.href = result.redirectTo;
      return;
    }
    setPageError(result?.error || t(9993, 'Unable to continue'));
    setProcessing(false);
  }, [dispatch, pendingId, t]);

  useEffect(() => {
    const load = async () => {
      try {
        setProcessing(true);
        const result = await api.pendingRegistration.getById(pendingId);
        if (!result?.success || !result?.data?.isPaid) {
          setPageError(result?.error || 'Payment registration was not found.');
          setProcessing(false);
          return;
        }

        const registration = result.data;
        if (registration.registrationFlow !== 'online_custom_content') {
          history.replace(`/finish-registration/${pendingId}`);
          return;
        }

        if (registration.isFinished) {
          await finishWithoutForm();
          return;
        }

        const learningCenterId = registration.learningCenterId || registration.purchaseId?.learningCenterId;
        const emailStatus = await api.pendingRegistration.emailStatus(
          learningCenterId ? { email: registration.email, learningCenterId } : { email: registration.email },
        );
        if (emailStatus?.exists) {
          await finishWithoutForm();
          return;
        }

        const precheck = await api.pendingRegistration.precheck(pendingId);
        if (!precheck?.success) {
          setPageError(precheck?.error || t(9993, 'Unable to continue'));
          setProcessing(false);
          return;
        }

        setPendingRegistration(registration);
        setValue('email', registration.email);
        setProcessing(false);
      } catch (error: any) {
        setPageError(error?.response?.data?.error || error?.message || t(9993, 'Unable to continue'));
        setProcessing(false);
      }
    };

    load();
  }, [finishWithoutForm, history, pendingId, setValue, t]);

  const onSubmit = async (values: Values) => {
    const result = await withLoading(
      dispatch,
      async () => api.pendingRegistration.finishRegistration(pendingId, {
        ...values,
        registerLocale: currentLanguage?.code || 'en',
      }),
    );

    if (result?.success && result?.redirectTo) {
      window.location.href = result.redirectTo;
      return;
    }

    if (result?.errors) {
      Object.entries(result.errors).forEach(([field, message]) => {
        if (field !== '_error' && message) {
          setError(field as keyof Values, { message: String(message) });
        }
      });
    }
    setPageError(result?.error || result?.errors?._error || t(9993, 'Unable to complete registration.'));
  };

  return (
    <div className={pageClasses.wrapper}>
      <RegisterHeader />
      <main className={pageClasses.main}>
        {processing && <p className={classes.status}>{t(9994, 'Processing...')}</p>}
        {!processing && pageError && <div className={classes.error}>{pageError}</div>}
        {!processing && pendingRegistration && (
          <div className={classes.formWrapper}>
            <h1 className={classes.title}>{t(866, 'Register')}</h1>
            <div className={classes.subtitle}>
              {t(864, 'Provide your email and/or mobile phone so we can send you tips for using family learning software.')}
            </div>

            <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={classes.topRow}>
                <div className={classes.contactColumn}>
                  <div className={classes.selectLocale}>
                    <p>{t(936, 'Do you want your messages in?')}</p>
                    <div className={classes.radios}>
                      {messageLocales.map((locale: LocaleOption) => (
                        <label key={locale.value}>
                          <input type="radio" value={locale.value} {...register('messageLocale')} />
                          <span>{locale.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Field
                    className={classes.emailField}
                    label={t(867, 'Email:')}
                    error={errors.email?.message}
                    inputProps={{
                      readOnly: true,
                      type: 'email',
                      ...register('email', { required: t(695, 'Required') }),
                    }}
                  />

                  {acceptsTexts && (
                    <Field
                      label={t(874, 'Phone:')}
                      error={errors.phone?.message}
                      inputProps={{
                        type: 'tel',
                        ...register('phone', {
                          pattern: { value: /\+1\d{10}/, message: 'Wrong phone number' },
                          required: acceptsTexts ? t(695, 'Required') : '',
                        }),
                      }}
                    />
                  )}

                  <label className={classes.checkbox}>
                    <input type="checkbox" {...register('agreeReceiveTextMessages')} />
                    <span>{t(937, 'I agree to receive text messages (only in the United States).')}</span>
                  </label>
                </div>
                <SupportPhone className={classes.phone} />
              </div>

              <div className={classes.familyGrid}>
                <div>
                  <Field
                    label={CUSTOM_REGISTRATION_COPY.adultFirstName}
                    error={errors.parentFirstName?.message}
                    inputProps={{
                      ...register('parentFirstName', {
                        required: t(695, 'Required'),
                        minLength: { value: 3, message: 'Min length 3 characters' },
                      }),
                    }}
                  />
                  <Field
                    label={CUSTOM_REGISTRATION_COPY.adultLastName}
                    error={errors.parentLastName?.message}
                    inputProps={{
                      ...register('parentLastName', {
                        required: t(695, 'Required'),
                        minLength: { value: 3, message: 'Min length 3 characters' },
                      }),
                    }}
                  />
                  <Field
                    label={CUSTOM_REGISTRATION_COPY.username}
                    error={errors.parentUsername?.message}
                    inputProps={{
                      ...register('parentUsername', {
                        required: t(695, 'Required'),
                        minLength: { value: 3, message: 'Min length 3 characters' },
                      }),
                    }}
                  />
                </div>
                <div>
                  <Field
                    label={CUSTOM_REGISTRATION_COPY.childFirstName}
                    error={errors.childFirstName?.message}
                    inputProps={{
                      ...register('childFirstName', {
                        required: t(695, 'Required'),
                        minLength: { value: 3, message: 'Min length 3 characters' },
                      }),
                    }}
                  />
                  <Field
                    label={CUSTOM_REGISTRATION_COPY.childLastName}
                    error={errors.childLastName?.message}
                    inputProps={{
                      ...register('childLastName', {
                        required: t(695, 'Required'),
                        minLength: { value: 3, message: 'Min length 3 characters' },
                      }),
                    }}
                  />
                  <Field
                    label={CUSTOM_REGISTRATION_COPY.username}
                    error={errors.childUsername?.message}
                    inputProps={{
                      ...register('childUsername', {
                        required: t(695, 'Required'),
                        validate: (value) => (
                          value === watch('parentUsername') ? 'Should be unique' : undefined
                        ),
                        minLength: { value: 3, message: 'Min length 3 characters' },
                      }),
                    }}
                  />
                </div>
              </div>

              <div className={classes.learningGrid}>
                <div className={classes.learningColumn}>
                  <h2>{CUSTOM_REGISTRATION_COPY.instructionLanguage}</h2>
                  <label className={classes.learningControl}>
                    <span>{CUSTOM_REGISTRATION_COPY.language}</span>
                    <select {...register('instructionLanguage', { required: t(695, 'Required') })}>
                      <option value="" disabled>{t(698, 'Select')}</option>
                      {instructionLocales.map((locale) => (
                        <option key={locale.value} value={locale.value}>{locale.label}</option>
                      ))}
                    </select>
                  </label>
                  {errors.instructionLanguage && (
                    <span className={classes.inputError}>{String(errors.instructionLanguage.message)}</span>
                  )}
                </div>
                <div className={classes.learningColumn}>
                  <h2>{CUSTOM_REGISTRATION_COPY.secondInstructionLanguage}</h2>
                  <p className={classes.learningDescription}>
                    {CUSTOM_REGISTRATION_COPY.secondLanguageDescription}
                  </p>
                  <label className={classes.learningControl}>
                    <span>{CUSTOM_REGISTRATION_COPY.language}</span>
                    <select {...register('secondInstructionLanguage', { required: t(695, 'Required') })}>
                      <option value="" disabled>{t(698, 'Select')}</option>
                      {instructionLocales.map((locale) => (
                        <option key={locale.value} value={locale.value}>{locale.label}</option>
                      ))}
                    </select>
                  </label>
                  {errors.secondInstructionLanguage && (
                    <span className={classes.inputError}>{String(errors.secondInstructionLanguage.message)}</span>
                  )}
                </div>
              </div>

              <div className={classes.beforeSubmit}>
                {t(865, 'After you click or tap the Submit button, make sure you bookmark the Login page.')}
              </div>

              <button
                disabled={isSubmitting || !instructionLocales.length}
                className={classes.submitBtn}
                type="submit"
              >
                {t(873, 'Submit')}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

const Field = ({
  label,
  error,
  inputProps,
  className = '',
}: {
  label: string;
  inputProps: HTMLProps<HTMLInputElement>;
  error?: string;
  className?: string;
}) => (
  <div className={`${classes.inputGroup} ${className}`.trim()}>
    <label>{label}</label>
    <input {...inputProps} />
    {error && <span className={classes.inputError}>{error}</span>}
  </div>
);

export default FinishCustomProductRegistration;
